import { apiService } from './api';
import { unwrapResponse } from './apiUtils';
import {
  Farm,
  CreateFarmDto,
  UpdateFarmDto,
  FarmListRequest,
  FarmListResponse
} from '../types/farms';
import { FilterOption } from '../types/options';
import { EntityDependencySummary } from '../types/entityDependencies';
import { DuplicateMatch } from '../types/duplicateMatch';

export const farmService = {
  // Slim { id, name } farm options for filter dropdowns (name = FarmName), ordered by name, optionally
  // scoped to a single farmer. Cacheable on the server (private, max-age=300 + ETag/304); avoids the
  // heavy paginated list fetch when a screen only needs to populate a farm filter/select.
  getOptions: async (farmerId?: number): Promise<FilterOption[]> => {
    const params = new URLSearchParams();
    if (farmerId) params.append('farmerId', farmerId.toString());
    const query = params.toString();

    const response = await apiService.get<FilterOption[]>(
      query ? `/farms/options?${query}` : '/farms/options'
    );

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farm options');
    }

    return response.data;
  },

  // Get paginated farms with search and filtering
  getFarms: async (request: FarmListRequest): Promise<FarmListResponse> => {
    const params = new URLSearchParams();

    if (request.pageNumber) params.append('pageNumber', request.pageNumber.toString());
    if (request.pageSize) params.append('pageSize', request.pageSize.toString());
    if (request.searchTerm) params.append('searchTerm', request.searchTerm);
    if (request.idFarmer) params.append('idFarmer', request.idFarmer.toString());
    if (request.sortBy) params.append('sortBy', request.sortBy);
    if (request.sortDescending !== undefined) params.append('sortDescending', request.sortDescending.toString());

    const response = await apiService.get<FarmListResponse>(`/farms?${params.toString()}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farms');
    }

    return response.data;
  },

  // Get farm by ID
  getById: async (id: number): Promise<Farm> => {
    const response = await apiService.get<Farm>(`/farms/${id}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch farm');
    }

    return response.data;
  },

  // Create new farm
  create: async (data: CreateFarmDto): Promise<Farm> => {
    const response = await apiService.post<Farm>('/farms', data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create farm');
    }

    return response.data;
  },

  // Update farm
  update: async (id: number, data: UpdateFarmDto): Promise<Farm> => {
    const response = await apiService.put<Farm>(`/farms/${id}`, data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update farm');
    }

    return response.data;
  },

  // Delete farm
  delete: async (id: number): Promise<void> => {
    const response = await apiService.delete<void>(`/farms/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete farm');
    }
  },

  // Delete pre-check: what references this farm + whether it can be deleted (server is authoritative).
  getDependencies: async (id: number): Promise<EntityDependencySummary> => {
    const response = await apiService.get<EntityDependencySummary>(`/farms/${id}/dependencies`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to check farm dependencies');
    }

    return response.data;
  },

  // Search farms
  search: async (searchTerm: string): Promise<Farm[]> => {
    const response = await apiService.get<Farm[]>(`/farms/search?searchTerm=${encodeURIComponent(searchTerm)}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to search farms');
    }

    return response.data;
  },

  // Create-time "did you mean?" nudge (Plan C, Layer 1): farms under this farmer matching the in-progress name.
  // Non-blocking — see useDuplicateCheck / DuplicateMatchPanel. Query built from defined params only.
  checkDuplicates: async (params: { farmerId: number; name: string; excludeId?: number }): Promise<DuplicateMatch[]> => {
    const query = new URLSearchParams();
    query.set('farmerId', String(params.farmerId));
    query.set('name', params.name);
    if (params.excludeId != null) query.set('excludeId', String(params.excludeId));

    const response = await apiService.get<DuplicateMatch[]>(`/farms/check-duplicates?${query.toString()}`);
    return unwrapResponse(response, 'Failed to check for duplicate farms');
  },
};
